/**
 * Bloco "Prazo prometido" do detalhe da solicitação: rota, prazo original
 * (imutável, régua da aderência), prazo vigente, estado derivado e
 * histórico de replanejamentos. Quem autoriza pode replanejar (nova data +
 * motivo) e ajustar a rota (com motivo) — formulários inline, padrão
 * Transition Form.
 */
import React, { useState } from "react";
import styled from "styled-components";

import AsyncButton from "../../../components/AsyncButton";
import { useAuth } from "../../../context/AuthContext";
import {
  MOTIVOS_REPLANEJAMENTO,
  ROTAS_PRAZO,
  ajustarRota,
  replanejarPrazo,
} from "../../../services/solicitacoes";
import { SectionTitle } from "../../../styles/shared";
import { formatarDataLocal } from "../../../utils/dinheiro";
import { dataHora } from "./helpers";
import PrazoChip from "./PrazoChip";
import {
  DetailGrid,
  DetailItem,
  DetailSection,
  Field,
  GhostBtn,
  InlineError,
  Input,
  Label,
  MotivoBox,
  PecaLinha,
  RowEnd,
  Select,
  TextArea,
} from "./styles";

const Historico = styled.ul`
  margin: 0;
  padding: 0 0 0 16px;
  font-size: 12px;
  color: var(--ink-2);
  li { margin: 2px 0; }
`;

const Grade = styled.div`
  display: grid;
  grid-template-columns: repeat(2, minmax(0, 1fr));
  gap: 8px 10px;
  @media (max-width: 520px) { grid-template-columns: 1fr; }
`;

const MOTIVO_REPLAN_LABEL = Object.fromEntries(MOTIVOS_REPLANEJAMENTO.map((m) => [m.value, m.label]));

function FormReplanejar({ familia, id, atual, onCancelar, onSalvo }) {
  const { request } = useAuth();
  const [novaData, setNovaData] = useState(atual || "");
  const [motivo, setMotivo] = useState("");
  const [texto, setTexto] = useState("");
  const [erro, setErro] = useState("");

  const salvar = async () => {
    setErro("");
    if (!novaData) { setErro("Informe a nova data."); throw new Error("data"); }
    if (!motivo) { setErro("Escolha o motivo do replanejamento."); throw new Error("motivo"); }
    try {
      await replanejarPrazo(request, familia, id, { nova_data: novaData, motivo, texto });
      onSalvo();
    } catch (e) {
      setErro(e?.message || "Não foi possível replanejar.");
      throw e;
    }
  };

  return (
    <MotivoBox>
      <Grade>
        <Field>
          <Label>Novo prazo de entrega</Label>
          <Input type="date" value={novaData} onChange={(e) => setNovaData(e.target.value)} />
        </Field>
        <Field>
          <Label>Motivo</Label>
          <Select value={motivo} onChange={(e) => setMotivo(e.target.value)} style={{ height: 38 }}>
            <option value="">— escolha —</option>
            {MOTIVOS_REPLANEJAMENTO.map((m) => <option key={m.value} value={m.value}>{m.label}</option>)}
          </Select>
        </Field>
      </Grade>
      <Field>
        <Label>Detalhe (opcional)</Label>
        <TextArea value={texto} onChange={(e) => setTexto(e.target.value)}
                  placeholder="Ex.: vidro do fornecedor só chega dia 25" />
      </Field>
      {erro && <InlineError>{erro}</InlineError>}
      <RowEnd>
        <GhostBtn type="button" onClick={onCancelar}>Voltar</GhostBtn>
        <AsyncButton variant="primary" silentError successMessage="Prazo replanejado." onClick={salvar}>
          Replanejar prazo
        </AsyncButton>
      </RowEnd>
    </MotivoBox>
  );
}

function FormRota({ familia, id, atual, onCancelar, onSalvo }) {
  const { request } = useAuth();
  const [rota, setRota] = useState(atual || "");
  const [motivo, setMotivo] = useState("");
  const [erro, setErro] = useState("");

  const salvar = async () => {
    setErro("");
    if (!rota) { setErro("Escolha a rota."); throw new Error("rota"); }
    if (!motivo.trim()) { setErro("Informe o motivo do ajuste."); throw new Error("motivo"); }
    try {
      await ajustarRota(request, familia, id, { rota, motivo: motivo.trim() });
      onSalvo();
    } catch (e) {
      setErro(e?.message || "Não foi possível ajustar a rota.");
      throw e;
    }
  };

  return (
    <MotivoBox>
      <Field>
        <Label>Rota</Label>
        <Select value={rota} onChange={(e) => setRota(e.target.value)} style={{ height: 38 }}>
          <option value="">— escolha —</option>
          {ROTAS_PRAZO.map((r) => <option key={r.value} value={r.value}>{r.label}</option>)}
        </Select>
      </Field>
      <Field>
        <Label>Motivo</Label>
        <TextArea value={motivo} onChange={(e) => setMotivo(e.target.value)}
                  placeholder="Ex.: a peça tem pintura, não é só corte" />
      </Field>
      {erro && <InlineError>{erro}</InlineError>}
      <RowEnd>
        <GhostBtn type="button" onClick={onCancelar}>Voltar</GhostBtn>
        <AsyncButton variant="primary" silentError successMessage="Rota ajustada; prazo recalculado." onClick={salvar}>
          Ajustar rota
        </AsyncButton>
      </RowEnd>
    </MotivoBox>
  );
}

export default function PrazoPanel({ familia, id, dado, podeEditar, onChanged }) {
  const [modo, setModo] = useState(null);
  if (!dado) return null;
  const temPrazo = Boolean(dado.prazo_entrega_em);
  const terminal = ["ATENDIDA", "RECUSADA", "CANCELADA"].includes(dado.situacao);
  const podeMexer = podeEditar && !terminal && dado.situacao !== "ABERTA";
  const replans = dado.prazo_replanejamentos || [];

  const salvo = () => { setModo(null); onChanged?.(); };

  return (
    <DetailSection>
      <SectionTitle>Prazo prometido</SectionTitle>
      <div><PrazoChip solicitacao={dado} /></div>
      {!temPrazo ? (
        <PecaLinha>
          {dado.situacao === "ABERTA"
            ? "O prazo nasce na autorização, pela rota das peças e pelas regras da unidade."
            : "Sem prazo registrado (autorizada antes das regras de prazo existirem)."}
        </PecaLinha>
      ) : (
        <DetailGrid>
          <DetailItem>
            <div className="lbl">Rota</div>
            <div className="val">{dado.prazo_rota_display || dado.prazo_rota || "—"}</div>
          </DetailItem>
          <DetailItem>
            <div className="lbl">Prazo original</div>
            <div className="val" title="Régua da aderência: não muda com replanejamento">
              {formatarDataLocal(dado.prazo_entrega_em)}
              {dado.prazo_setor_em ? ` · setor até ${formatarDataLocal(dado.prazo_setor_em)}` : ""}
            </div>
          </DetailItem>
          <DetailItem>
            <div className="lbl">Prazo vigente</div>
            <div className="val">
              {formatarDataLocal(dado.prazo_entrega_vigente_em)}
              {dado.replanejado ? " (replanejado)" : ""}
            </div>
          </DetailItem>
          <DetailItem>
            <div className="lbl">Situação do prazo</div>
            <div className="val">
              {dado.estado_prazo_display || "—"}
              {typeof dado.atraso_dias_uteis === "number" && dado.atraso_dias_uteis > 0
                ? ` · ${dado.atraso_dias_uteis} d.u. de atraso`
                : ""}
            </div>
          </DetailItem>
        </DetailGrid>
      )}

      {replans.length > 0 && (
        <Historico>
          {replans.map((r, i) => (
            <li key={i}>
              {dataHora(r.em)} · {formatarDataLocal(r.de)} para {formatarDataLocal(r.para)} ·{" "}
              {MOTIVO_REPLAN_LABEL[r.motivo] || r.motivo}
              {r.texto ? ` — ${r.texto}` : ""}
              {r.por_nome ? ` (${r.por_nome})` : ""}
            </li>
          ))}
        </Historico>
      )}

      {podeMexer && !modo && (
        <RowEnd style={{ justifyContent: "flex-start" }}>
          <GhostBtn type="button" onClick={() => setModo("replanejar")} disabled={!temPrazo}>
            Replanejar prazo
          </GhostBtn>
          <GhostBtn type="button" onClick={() => setModo("rota")}>
            Ajustar rota
          </GhostBtn>
        </RowEnd>
      )}
      {modo === "replanejar" && (
        <FormReplanejar familia={familia} id={id} atual={dado.prazo_entrega_vigente_em}
                        onCancelar={() => setModo(null)} onSalvo={salvo} />
      )}
      {modo === "rota" && (
        <FormRota familia={familia} id={id} atual={dado.prazo_rota}
                  onCancelar={() => setModo(null)} onSalvo={salvo} />
      )}
    </DetailSection>
  );
}
